/** @file Rebuilds the skeleton and overlay chunks of a posed scene and lists every chunk to draw. */
import type { Pose, SkinData } from '@pose2d/core/types';
import { buildRender } from '@pose2d/geometry/buildRender';
import type { Camera, RenderBuffers, TexturePages } from '@pose2d/geometry/types';
import { buildOverlay } from '@pose2d/overlay/buildOverlay';
import { createOverlayChunk } from '@pose2d/overlay/createOverlayChunk';
import type { FontAtlas, OverlayInput, OverlayOptions } from '@pose2d/overlay/types';
import type { Scene } from './createScene';
import { fadeChunk } from './fadeChunk';
import type { GhostScene } from './ghostScene';

/** One frame's drawing inputs; `gizmoChunks` = how many of `scene.gizmo` drawGizmo filled; `ghost` = ghostStore's. */
export interface FrameInput {
  textures: TexturePages; camera: Camera; options: OverlayOptions; font: FontAtlas; onionPoses: Pose[]; gizmoChunks: number;
  ghost: GhostScene | null;
}

const NO_SKIN: SkinData = { name: '', map: new Map() };
const allocate = (): RenderBuffers => createOverlayChunk();

/** The ghost's attachment chunk, then its overlay chunks rebuilt for this camera at the ghost's opacity (W3-33). */
function pushGhost(ghost: GhostScene, input: FrameInput, out: RenderBuffers[]): void {
  const { camera, textures, font } = input;
  const used = buildOverlay(ghost.overlay, { pose: ghost.pose, data: ghost.data, camera, textures, options: ghost.options, font, allocate });
  const overlay = ghost.overlay.slice(0, used);
  for (const chunk of overlay) fadeChunk(chunk, ghost.opacity);
  out.push(ghost.skeleton, ...overlay);
}

/** Fills `out` (cleared first): skeleton, used overlay chunks, the ghost's chunks, gizmo chunks; returns `out`. */
export function frameChunks(scene: Scene, input: FrameInput, out: RenderBuffers[]): RenderBuffers[] {
  const { data, pose } = scene;
  buildRender(scene.skeleton, { pose, data, skin: data.skins[data.defaultSkin] ?? NO_SKIN, textures: input.textures, splitRuns: 'slot' });
  const overlay: OverlayInput = {
    pose, data, camera: input.camera, textures: input.textures, options: input.options, font: input.font, allocate,
    ...(input.onionPoses.length > 0 ? { onionPoses: input.onionPoses } : {}),
  };
  const used = buildOverlay(scene.overlay, overlay);
  out.length = 0;
  out.push(scene.skeleton, ...scene.overlay.slice(0, used));
  if (input.ghost !== null) pushGhost(input.ghost, input, out);
  out.push(...scene.gizmo.slice(0, input.gizmoChunks));
  return out;
}
